import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import { useSnackbar } from 'notistack';
import useGetCaySam, { useCamera } from '../hooks/use-get-caysam'; // Import default hook và useCamera

type CameraViewProps = {
  vuongSamId?: number;
  loSamId?: number;
};

// Component chính
export default function CameraViewer({ 
  vuongSamId,
  loSamId 
}: CameraViewProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { 
    cameras, 
    loadingCameras, 
    getCamerasByLoSamId 
  } = useGetCaySam();

  const { streamUri, cameraState, startCamera } = useCamera();

  const [selectedCameraId, setSelectedCameraId] = useState<number | null>(null);
  const [isStartingCamera, setIsStartingCamera] = useState(false);

  useEffect(() => {
    if (loSamId) {
      getCamerasByLoSamId(loSamId.toString());
    }
  }, [loSamId, getCamerasByLoSamId]);

  useEffect(() => {
    if (streamUri && selectedCameraId && !isStartingCamera) {
      const camera = cameras.find(c => c.id === selectedCameraId);
      if (camera) {
        navigate(paths.camera.root, {
          state: {
            vuongSamId,
            loSamId: camera.loSamId, // Hoặc dùng props loSamId
            camera,
            cameraId: selectedCameraId,
            streamUri,
            cameraState
          },
        });
      }
    }
  }, [cameras, navigate, selectedCameraId, streamUri, cameraState, vuongSamId, isStartingCamera]); 

  const handleCameraSelect = async (cameraId: number) => {
    const camera = cameras.find(c => c.id === cameraId);
    if (!camera) return;

    setSelectedCameraId(cameraId);
    setIsStartingCamera(true);

    try {
      await startCamera(camera);
    } catch (error) {
      console.error('Error starting camera:', error);
      enqueueSnackbar('Không thể khởi động camera', { variant: 'error' });
      setSelectedCameraId(null); 
    } finally {
        setIsStartingCamera(false);
    }
  };
 
  if (loadingCameras) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Xử lý khi không có camera
  if (!cameras || cameras.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          <Iconify icon="solar:camera-bold" sx={{ mr: 1, verticalAlign: 'middle' }} />
          Camera Giám Sát Lô Sâm
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Chưa có camera nào cho lô sâm này
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{mb:2}}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        <Iconify icon="solar:camera-bold" sx={{ mr: 1, verticalAlign: 'middle' }} />
        Camera Giám Sát Lô Sâm
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }} disabled={isStartingCamera}>
        <InputLabel>
          {isStartingCamera ? 'Đang kết nối camera...' : 'Chọn camera'}
        </InputLabel>
        
        <Select
          value={selectedCameraId || ''}
          label={isStartingCamera ? 'Đang kết nối camera...' : 'Chọn camera'}
          onChange={(e) => handleCameraSelect(Number(e.target.value))}
          IconComponent={
            isStartingCamera 
              ? () => <CircularProgress size={20} sx={{margin:2}} />   
              : undefined
          }
        >
          {cameras.map((camera) => (
            <MenuItem key={camera.id} value={camera.id}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {camera.loSamLoaiCamera?.ten || `Camera ${camera.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lô sâm {camera.loSamId}
                  </Typography>
                
                </Box>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}